/**
 * Engine-picker options — projects the data-driven {@link ModelRegistry} into a
 * flat option list for the wizard/iterate engine <select>. A superadmin's
 * `platformSettings.defaultEngine` can be threaded in as the default selection;
 * the registry is the source of the catalog.
 */
import { ModelRegistry, type ModelEntry } from '@djgg/llm';
import type { EngineRef } from '@djgg/creations';

/** One selectable engine option. */
export interface EngineOption {
  /** Stable value for the <select> (the model id). */
  value: string;
  label: string;
  engine: EngineRef;
  tier: ModelEntry['tier'];
}

const registry = new ModelRegistry();

/** All engine options for the picker (registry order). */
export function engineOptions(): EngineOption[] {
  return registry.list().map((e) => ({
    value: e.id,
    label: `${e.displayName} (${e.provider})`,
    engine: { provider: e.provider, model: e.id },
    tier: e.tier,
  }));
}

/** Resolve a model id back to an {@link EngineRef}, or undefined. */
export function engineForId(id: string): EngineRef | undefined {
  const entry = registry.get(id);
  return entry ? { provider: entry.provider, model: entry.id } : undefined;
}

/** The default model id for the build task (honors no override here — a plain
 *  seed default; the superadmin override lives server-side). */
export function defaultBuildModelId(): string {
  return registry.defaultFor('build').id;
}
